import {
  ValuationPreview,
  ValuationConcept,
} from '@/interfaces/valuation/ValuationRecord'
import client from '@/services/api/client'
import createMessage from '@/services/app/createMessage'
import { MessageType } from '@/interfaces/app/Message'
import { MutationTree, ActionTree, GetterTree, Module } from 'vuex'
import {
  ValuationRecordsState,
  ValuationRecordsPagination,
} from '@/types/store/valuation'
import { RootState } from '@/types/store'

const mutations: MutationTree<ValuationRecordsState> = {
  setPreviews(state, data: ValuationPreview[]) {
    state.previews = data
  },
  setPagination(state, pagination: ValuationRecordsPagination) {
    state.pagination = pagination
  },
  setPaginationPages(
    state,
    {
      first,
      current,
      last,
    }: { first?: number; current?: number; last?: number }
  ) {
    state.pagination.first = first || state.pagination.first
    state.pagination.current = current || state.pagination.current
    state.pagination.last = last || state.pagination.last
  },
  setPaginationPerPage(state, perPage: number) {
    state.pagination.perPage = perPage
  },
  setConcepts(state, data: ValuationConcept[]) {
    state.concepts = data
  },
  setConceptPaginationPages(
    state,
    {
      first,
      current,
      last,
    }: { first?: number; current?: number; last?: number }
  ) {
    state.conceptPagination.first = first || state.conceptPagination.first
    state.conceptPagination.current = current || state.conceptPagination.current
    state.conceptPagination.last = last || state.conceptPagination.last
  },
  setConceptPaginationPerPage(state, perPage: number) {
    state.conceptPagination.perPage = perPage
  },
}

const actions: ActionTree<ValuationRecordsState, RootState> = {
  async fetchPreviews({ commit, dispatch, state }) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/valuation`,
      params: {
        per_page: state.pagination.perPage,
        page: state.pagination.current,
      },
      method: 'GET',
    })
      .then(previewsData => {
        commit('setPaginationPages', {
          first: previewsData.meta.from,
          last: previewsData.meta.last_page,
        })
        const previews: ValuationPreview[] = []
        for (const preview of previewsData.data) {
          previews.push(new ValuationPreview(preview))
        }
        commit('setPreviews', previews)
      })
      .catch(err => {
        const message = createMessage(
          'Valuace se nepodařilo načíst. Zkuste to prosím později.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3500 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  changePage({ commit, dispatch, state }, pageNumber: number) {
    if (pageNumber > state.pagination.last) {
      pageNumber = state.pagination.last
    }

    commit('setPaginationPages', { current: pageNumber })
    dispatch('fetchPreviews')
  },
  changePerPage({ commit, dispatch }, perPageCount: number) {
    commit('setPaginationPages', { current: 1 })
    commit('setPaginationPerPage', perPageCount)
    dispatch('fetchPreviews')
  },
  async fetchConcepts({ commit, dispatch, state }) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/concept`,
      params: {
        per_page: state.conceptPagination.perPage,
        page: state.conceptPagination.current,
      },
      method: 'GET',
    })
      .then(conceptsData => {
        commit('setConceptPaginationPages', {
          first: conceptsData.meta.from,
          last: conceptsData.meta.last_page,
        })
        const concepts: ValuationConcept[] = []
        for (const concept of conceptsData.data) {
          concepts.push(new ValuationConcept(concept))
        }
        commit('setConcepts', concepts)
      })
      .catch(err => {
        const message = createMessage(
          'Valuace se nepodařilo načíst. Zkuste to prosím později.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3500 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  changeConceptPage({ commit, dispatch, state }, pageNumber: number) {
    if (pageNumber > state.conceptPagination.last) {
      pageNumber = state.conceptPagination.last
    }

    commit('setConceptPaginationPages', { current: pageNumber })
    dispatch('fetchConcepts')
  },
  changeConceptPerPage({ commit, dispatch }, perPageCount: number) {
    commit('setConceptPaginationPages', { current: 1 })
    commit('setConceptPaginationPerPage', perPageCount)
    dispatch('fetchConcepts')
  },
}

const getters: GetterTree<ValuationRecordsState, RootState> = {
  previews: (state): ValuationPreview[] => state.previews,
  pagination: (state): ValuationRecordsPagination => state.pagination,
  concepts: (state): ValuationConcept[] => state.concepts,
  conceptPagination: (state): ValuationRecordsPagination =>
    state.conceptPagination,
}

export const valuationRecords: Module<ValuationRecordsState, RootState> = {
  namespaced: true,
  state: {
    previews: [],
    concepts: [],
    pagination: {
      perPage: 10,
      current: 1,
      first: 1,
      last: 1,
    },
    conceptPagination: {
      perPage: 10,
      current: 1,
      first: 1,
      last: 1,
    },
  },
  mutations,
  actions,
  getters,
}

export default valuationRecords
